import { createTheme } from '@mui/material';

export const DarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ec407a',
      dark: '#d81b60',
      light: '#f48fb1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f5f5f5',
      dark: '#e0e0e0',
      light: '#ffffff',
      contrastText: '#0d1117',
    },
    background: {
      default: '#0d1117',
      paper: '#161b22'
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    text: {
      primary: '#e6edf3',
      secondary: '#7d8590',
      disabled: '#484f58',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    allVariants: {
      color: '#e6edf3',
    },
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    subtitle2: {
      fontSize: '0.6875rem',
      fontWeight: 500,
      letterSpacing: '0.04em',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          scrollbarWidth: 'thin',
          scrollbarColor: '#30363d #0d1117',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#0d1117',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#30363d',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: '#484f58',
            },
          },
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(236, 64, 122, 0.3)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #ec407a 0%, #d81b60 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f48fb1 0%, #ec407a 100%)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
        sizeSmall: {
          padding: '6px 14px',
          fontSize: '0.75rem',
          borderRadius: '8px',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
          borderRadius: '12px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.04)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: '16px',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
          background: '#161b22',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              boxShadow: '0 0 0 3px rgba(236, 64, 122, 0.15)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '9999px',
          fontWeight: 600,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          borderRadius: '8px 8px 0 0',
          '&.Mui-selected': {
            color: '#ec407a',
          },
          '&:hover': {
            backgroundColor: 'rgba(236, 64, 122, 0.08)',
            borderRadius: '8px 8px 0 0',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#ec407a',
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            fontSize: '1.25rem !important',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            padding: '6px',
          },
        },
        sizeSmall: {
          '@media (max-width: 600px)': {
            padding: '4px',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          '@media (max-width: 600px)': {
            fontSize: '1.5rem',
          },
        },
        h2: {
          '@media (max-width: 600px)': {
            fontSize: '1.25rem',
          },
        },
        h3: {
          '@media (max-width: 600px)': {
            fontSize: '1.125rem',
          },
        },
        h4: {
          '@media (max-width: 600px)': {
            fontSize: '1rem',
          },
        },
        h5: {
          '@media (max-width: 600px)': {
            fontSize: '0.875rem',
          },
        },
        h6: {
          '@media (max-width: 600px)': {
            fontSize: '0.8rem',
          },
        },
        body1: {
          '@media (max-width: 600px)': {
            fontSize: '0.8rem',
          },
        },
        body2: {
          '@media (max-width: 600px)': {
            fontSize: '0.75rem',
          },
        },
      },
    },
  },
});
